import { axiosInstance } from '../../../service/axiosManager'
import { getOffloadingSummary } from '../../../service/experimentService'
import { computeAnalyticsById } from '../../instructorDashboard/context/StudentAnalyticsContext'
import { buildSessionDataset, downloadSessionCSV } from '../../instructorDashboard/components/buildSessionDataset'
import { buildQueryDataset, downloadQueryCSV } from '../../instructorDashboard/components/buildQueryDataset'
import { conditionTag, publishedOffloading, studyConditionId } from './experimentConfig'

// One CSV for every condition of an experiment: the Monitor's own session-level or
// query-level export (ConversationsModal), built per condition session and joined, with the
// condition filled in on every row - `condition` (its name), `condition_id` (the classroom
// study's id for it, A1-A8 or N; empty outside the study), `experiment_group` (its tag;
// this column is empty in a single-session export) and `deployment` (the builders' spare
// column, also the condition name).

const API = process.env.REACT_APP_HOST_API

const BUILDERS = {
  session: { build: buildSessionDataset, download: downloadSessionCSV },
  query: { build: buildQueryDataset, download: downloadQueryCSV },
}

// The same inputs the Monitor's export gets: the students it lists (its roster, narrowed to
// students with activity - the filter computeAnalyticsById applies, like InstructorDashboard's
// filteredStudents), the session's telemetry, and the per-student analytics.
//
// Fetched directly rather than through getAllUsersInSession / getTelemetryEventsBySession,
// which turn a failed request into an empty result: here that would silently leave a whole
// condition out of the joined file.
const loadConditionInputs = async (sessionId) => {
  const [usersResponse, telemetryResponse] = await Promise.all([
    axiosInstance.get(`${API}/users/session/${sessionId}`),
    axiosInstance.get(`${API}/telemetry/session/${sessionId}`, { timeout: 60000 }),
  ])
  const eventsByUser = telemetryResponse.data?.eventsByUser || {}
  const { analyticsById } = computeAnalyticsById(eventsByUser)
  const students = (Array.isArray(usersResponse.data) ? usersResponse.data : []).filter(
    (u) => (u.role === 3 || u.role === 4) && analyticsById[String(u.id)]
  )
  return { students, eventsByUser, analyticsById }
}

// level: 'session' | 'query'. Resolves to the number of rows downloaded (0 = nothing to
// download yet, no file is written). Rejects - naming the condition (error.conditionName) -
// when any condition's data cannot be loaded, so no file with a condition missing is written.
// Whether any condition runs the offloading analysis (its labels go into the exports).
export const usesOffloading = (conditions) => conditions.some((c) => publishedOffloading(c) !== 'off')

const fileSlug = (experimentName, experimentId) =>
  String(experimentName || `experiment_${experimentId}`).trim().replace(/[^A-Za-z0-9]+/g, '_').replace(/^_|_$/g, '') ||
  'experiment'

export const downloadExperimentDataset = async ({ experimentId, experimentName, conditions, level, withOffloading = true }) => {
  const builder = BUILDERS[level]
  if (!builder) throw new Error(`Unknown export level: ${level}`)
  // The session-level file also gets each student's offloading summary - when it can be had:
  // the file itself must not fail for it. With `withOffloading` false (the Overview's file,
  // nothing from the AI's labels) it is not fetched, and the file has no offloading columns.
  let offloading = null
  if (level === 'session' && withOffloading && usesOffloading(conditions)) {
    try {
      offloading = await getOffloadingSummary(experimentId)
    } catch (error) {
      console.warn('Session-level export without the offloading columns:', error)
    }
  }
  const offloadingOf = (condition, studentId) => {
    if (!offloading) return {}
    const summary = offloading.conditions.find((c) => c.key === condition.key)?.students?.[String(studentId)]
    const labelOf = (id) => offloading.concepts.find((concept) => concept.id === id)?.label || id
    return {
      offloaded_concepts: (summary?.offloaded || []).map(labelOf).join('; '),
      offloaded_exchanges: summary?.offloadedTurns ?? 0,
      analyzed_exchanges: summary?.analyzed ?? 0,
      learn_more_requests: Object.values(summary?.learnMore || {}).reduce((sum, n) => sum + n, 0),
    }
  }
  const perCondition = await Promise.all(
    conditions.map(async (condition) => {
      let inputs
      try {
        inputs = await loadConditionInputs(condition.sessionId)
      } catch (error) {
        throw Object.assign(error, { conditionName: condition.name })
      }
      const { students, eventsByUser, analyticsById } = inputs
      const rows = await builder.build(students, eventsByUser, condition.sessionId, analyticsById, condition.name)
      return rows.map((row) => ({
        condition: condition.name,
        condition_id: studyConditionId(condition),
        ...row,
        experiment_group: conditionTag(experimentId, condition.key),
        ...offloadingOf(condition, row.student_id),
      }))
    })
  )
  const rows = perCondition.flat()
  if (!rows.length) return 0
  builder.download(rows, `${fileSlug(experimentName, experimentId)}_${level}_level.csv`)
  return rows.length
}

// The offloading labels: one row per labeled exchange and concept (an exchange about none of
// the concepts gets one row without a concept), with the model's evidence. Resolves to the
// number of rows downloaded (0 = nothing labeled yet, no file).
export const downloadOffloadingDataset = async ({ experimentId, experimentName, conditions }) => {
  const summary = await getOffloadingSummary(experimentId, { exchanges: true })
  const exchanges = summary.exchanges || []
  if (!exchanges.length) return 0
  // Student names from each condition's roster, as in the other exports.
  const rosters = await Promise.all(
    conditions.map(async (condition) => {
      try {
        const response = await axiosInstance.get(`${API}/users/session/${condition.sessionId}`)
        return Array.isArray(response.data) ? response.data : []
      } catch (error) {
        throw Object.assign(error, { conditionName: condition.name })
      }
    })
  )
  const nameOf = new Map(rosters.flat().map((u) => [Number(u.id), [u.first_name, u.last_name].filter(Boolean).join(' ')]))
  const conditionByKey = new Map(conditions.map((c) => [c.key, c]))
  const rows = exchanges.map((exchange) => ({
    condition: conditionByKey.get(exchange.condition_key)?.name || exchange.condition_key,
    condition_id: studyConditionId(conditionByKey.get(exchange.condition_key)),
    experiment_group: conditionTag(experimentId, exchange.condition_key),
    session_id: exchange.session_id,
    student_id: exchange.user_id,
    student_name: nameOf.get(Number(exchange.user_id)) || '',
    message_id: exchange.message_id,
    asked_at: exchange.asked_at,
    student_message: exchange.student_message,
    concept_id: exchange.concept_id,
    concept: exchange.concept,
    status: exchange.status,
    evidence: exchange.evidence,
    model: exchange.model,
  }))
  downloadSessionCSV(rows, `${fileSlug(experimentName, experimentId)}_offloading.csv`)
  return rows.length
}
